/** Minute-of-day window [startMin, endMin) in session-type local calendar day.
 * endMin may exceed 1440 for an overnight window so a slot can start on this
 * day and finish after midnight (grouped by slot start date). */

import {
  dayOfWeekForYmd,
  isDayAllowed,
  rulesFromSessionType,
} from './schedulingRules';

export type BookingDayIntervalMin = {
  startMin: number;
  endMin: number;
  /** Evening start so the morning tail continues the same overnight grid. */
  alignToMin?: number;
};
export const MINUTES_PER_DAY = 24 * 60;

/** Stringify a primitive booking-window field value; non-primitives fall back to ''. */
function safeString(value: unknown): string {
  return typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
    ? String(value)
    : '';
}

/** Wall-clock string from a TIME / ISO / Date value (`HH:MM` or `HH:MM:SS`). */
function dailyTimeFieldToString(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return safeString(value).trim();
}

function dailyTimeToMinutes(t: string): number | null {
  const s = String(t).trim();
  if (!s) return null;
  const iso = /(?:T|^|\s)(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (!iso) {
    const hourOnly = /^(\d{1,2})$/.exec(s);
    if (!hourOnly) return null;
    const hOnly = parseInt(hourOnly[1], 10);
    if (Number.isNaN(hOnly) || hOnly > 24) return null;
    return hOnly === 24 ? MINUTES_PER_DAY : hOnly * 60;
  }
  const h = parseInt(iso[1], 10);
  const m = parseInt(iso[2], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h === 24 && m === 0) return MINUTES_PER_DAY;
  if (h === 24 || h > 23) return null;
  return h * 60 + m;
}

/** Calendar YYYY-MM-DD from a session-type DATE (string, ISO, or Date). */
export function ymdFromSessionTypeDate(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const raw = safeString(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // DATE serialized at UTC midnight: keep the prefix, do not shift by session TZ.
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?Z$/i.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  // Local-midnight DATE in a positive offset (e.g. IST) serializes as the
  // previous UTC calendar day at 18:30Z. Anchor +12h to recover the DATE.
  const anchored = new Date(parsed.getTime() + 12 * 60 * 60 * 1000);
  const y = anchored.getUTCFullYear();
  const m = String(anchored.getUTCMonth() + 1).padStart(2, '0');
  const d = String(anchored.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function sessionTypeDate(
  sessionType: Record<string, unknown>,
  kind: 'start' | 'end',
): string {
  const candidates =
    kind === 'start'
      ? [
          sessionType.start_date,
          sessionType.sessionTypeStartDate,
          sessionType.schedulingStartDate,
        ]
      : [
          sessionType.end_date,
          sessionType.sessionTypeEndDate,
          sessionType.schedulingEndDate,
        ];
  return candidates.map(ymdFromSessionTypeDate).find(Boolean) ?? '';
}

/** Overnight window may run past midnight so a slot can finish the next day. */
function overnightCompleteByMin(endMin: number): number {
  return endMin > 0 ? MINUTES_PER_DAY + endMin : MINUTES_PER_DAY;
}

function shiftYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

function isYmdAllowedForOvernight(
  sessionType: Record<string, unknown>,
  ymd: string,
): boolean {
  const rules = rulesFromSessionType({
    scheduling_rules:
      sessionType.scheduling_rules ?? sessionType.schedulingRules,
  });
  return isDayAllowed(rules, dayOfWeekForYmd(ymd), ymd);
}

/**
 * An overnight shift begins on this day only when the following calendar day
 * is also bookable, so the morning tail has a date to land on (Option A).
 */
function overnightStartsOnDate(
  sessionType: Record<string, unknown>,
  ymd: string,
  startDate: string,
  endDate: string,
): boolean {
  if (!ymd || ymd < startDate || ymd >= endDate) return false;
  const nextYmd = shiftYmd(ymd, 1);
  if (nextYmd < startDate || nextYmd > endDate) return false;
  return (
    isYmdAllowedForOvernight(sessionType, ymd) &&
    isYmdAllowedForOvernight(sessionType, nextYmd)
  );
}

function overnightIntervalsForDate(
  sessionType: Record<string, unknown>,
  startMin: number,
  endMin: number,
  selectedDate: string,
  startDate: string,
  endDate: string,
): BookingDayIntervalMin[] {
  if (
    !selectedDate ||
    !startDate ||
    !endDate ||
    endDate <= startDate ||
    selectedDate < startDate ||
    selectedDate > endDate
  ) {
    return [];
  }
  if (startMin === endMin) {
    return selectedDate > startDate && selectedDate < endDate
      ? [{ startMin: 0, endMin: MINUTES_PER_DAY }]
      : [];
  }

  const intervals: BookingDayIntervalMin[] = [];
  // Morning tail belongs to the day AFTER an overnight start (Option A).
  if (
    endMin > 0 &&
    overnightStartsOnDate(
      sessionType,
      shiftYmd(selectedDate, -1),
      startDate,
      endDate,
    )
  ) {
    intervals.push({ startMin: 0, endMin, alignToMin: startMin });
  }
  // Evening (and slots that cross midnight) belong to the start date.
  if (overnightStartsOnDate(sessionType, selectedDate, startDate, endDate)) {
    intervals.push({ startMin, endMin: overnightCompleteByMin(endMin) });
  } else if (isYmdAllowedForOvernight(sessionType, selectedDate)) {
    // Last day of the campaign or of a working-day stretch: the next morning
    // is not bookable, but slots that start this evening and finish before
    // midnight (8PM–11PM) are still valid.
    intervals.push({ startMin, endMin: MINUTES_PER_DAY });
  }
  return intervals;
}

/**
 * Booking window from session type `booking_daily_*` fields (wall times in session type TZ).
 * If both unset → full calendar day. Cross-midnight ranges are split around
 * midnight and returned only for the segment belonging to `selectedDate`.
 * Evening segments extend past 1440 so a slot that *starts* on that date can
 * finish after midnight (Option A: group by slot start date).
 */
export function getSessionTypeBookingDayIntervals(
  sessionType: Record<string, unknown>,
  selectedDate = '',
): BookingDayIntervalMin[] {
  const rawStart =
    sessionType.booking_daily_start_time ?? sessionType.bookingDailyStartTime;
  const rawEnd =
    sessionType.booking_daily_end_time ?? sessionType.bookingDailyEndTime;
  const startS = dailyTimeFieldToString(rawStart);
  const endS = dailyTimeFieldToString(rawEnd);

  const fullDay = (): BookingDayIntervalMin[] => [
    { startMin: 0, endMin: MINUTES_PER_DAY },
  ];

  if (!startS && !endS) return fullDay();

  const startMinRaw = dailyTimeToMinutes(startS || '00:00');
  const startMin =
    startMinRaw == null ? 0 : Math.max(0, Math.min(MINUTES_PER_DAY, startMinRaw));

  let endMin: number;
  if (!endS) {
    endMin = MINUTES_PER_DAY;
  } else {
    const em = dailyTimeToMinutes(endS);
    endMin = em == null ? MINUTES_PER_DAY : Math.max(0, Math.min(MINUTES_PER_DAY, em));
  }

  if (endMin <= startMin) {
    return overnightIntervalsForDate(
      sessionType,
      startMin,
      endMin,
      selectedDate,
      sessionTypeDate(sessionType, 'start'),
      sessionTypeDate(sessionType, 'end'),
    );
  }

  return [{ startMin, endMin }];
}

/** HH:MM on a 24h clock; minutes past 1440 wrap (1500 → 01:00). */
export function formatMinuteOfDayHm(totalMin: number): string {
  const normalized =
    ((totalMin % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function shiftLocalDateByDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function wallClockFromMinuteOfDay(totalMin: number): {
  dayOffset: number;
  hour: number;
  minute: number;
} {
  const dayOffset = Math.floor(totalMin / MINUTES_PER_DAY);
  const normalized =
    ((totalMin % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return {
    dayOffset,
    hour: Math.floor(normalized / 60),
    minute: normalized % 60,
  };
}

/** First slot start on this interval, aligned to an overnight evening grid. */
export function firstAlignedSessionStartMin(
  interval: BookingDayIntervalMin,
  bufferBefore: number,
  increment: number,
): number {
  const raw = interval.startMin + bufferBefore;
  const alignTo = interval.alignToMin;
  if (alignTo == null || increment <= 0) return raw;
  let tick = alignTo + bufferBefore;
  if (tick >= MINUTES_PER_DAY) {
    return Math.max(raw, tick - MINUTES_PER_DAY);
  }
  const steps = Math.ceil((MINUTES_PER_DAY - tick) / increment);
  return Math.max(raw, tick + steps * increment - MINUTES_PER_DAY);
}

/** Slot start must fall on the queried calendar day (Option A). */
export function sessionStartFitsSelectedDay(
  sessionStartMin: number,
  sessionDuration: number,
  intervalEndMin: number,
  bufferAfter: number,
): boolean {
  return (
    sessionStartMin < MINUTES_PER_DAY &&
    sessionStartMin + sessionDuration <= intervalEndMin - bufferAfter
  );
}

export function utcFromSelectedDateMinutes(
  selectedDate: Date,
  totalMin: number,
  timezone: string | undefined,
  dateTimeToUTC: (
    date: Date,
    hours: number,
    minutes: number,
    timezone?: string,
  ) => Date,
): Date {
  const { dayOffset, hour, minute } = wallClockFromMinuteOfDay(totalMin);
  return dateTimeToUTC(
    shiftLocalDateByDays(selectedDate, dayOffset),
    hour,
    minute,
    timezone,
  );
}

/** End clock earlier than start means the slot finished the next calendar day. */
export function localDateForSlotEnd(
  slotDate: Date,
  startHm: string,
  endHm: string,
): Date {
  const startMin = dailyTimeToMinutes(startHm);
  const endMin = dailyTimeToMinutes(endHm);
  if (startMin == null || endMin == null) return slotDate;
  if (endMin <= startMin) return shiftLocalDateByDays(slotDate, 1);
  return slotDate;
}
